import { useCallback, useEffect } from "react";

import { JobDetailsSkeleton } from "components/job/JobDetailsSkeleton";
import { Outlet, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { setPage, setSelectedJob } from "store/root.store";
import {
  getComments,
  getFiles,
  getLinks,
  getNotes,
  getPlannerJob,
  getVisits,
  selectJobLoaded,
  setJobTab,
  setPlannerSelectedJob,
} from "store/slices/planner.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { isFlagEnabled } from "store/slices/user.store";

interface JobProviderProps {
  jobId: string | undefined;
}

export default function PlannerJobProvider({ jobId }: JobProviderProps) {
  const dispatch = useAppDispatch();
  const jobLoaded = useAppSelector(selectJobLoaded);
  const { state }: any = useLocation();
  const commentsEnabled = useAppSelector((s) => isFlagEnabled(s, "Comments"));
  const documentWebLinksEnabled = useAppSelector((s) => isFlagEnabled(s, "DocumentWebLinks"));

  const initializeJob = useCallback(async () => {
    await dispatch(getPlannerJob()).catch(() => {
      dispatch(addSnackbarMessage({ key: "GetJob-fail" }));
    });
    dispatch(getFiles());
    dispatch(getNotes());
    dispatch(getVisits()).catch(() => {
      dispatch(addSnackbarMessage({ key: "GetJobVisits-fail" }));
    });
    if (commentsEnabled) dispatch(getComments());
    if (documentWebLinksEnabled) dispatch(getLinks());
  }, [commentsEnabled, dispatch, documentWebLinksEnabled]);

  useEffect(() => {
    if (!jobId) return;
    dispatch(setPage({ page: "planner" }));
    dispatch(setJobTab({ tab: "details" }));
    dispatch(setPlannerSelectedJob({ jobId }));
    dispatch(setSelectedJob({ jobId }));
    initializeJob();
  }, [dispatch, initializeJob, jobId]);

  useEffect(() => {
    if (!jobLoaded) return;
    if (state?.openComment) dispatch(setJobTab({ tab: "comments" }));
  }, [dispatch, jobLoaded, state?.openComment]);

  if (!jobLoaded) {
    return <JobDetailsSkeleton />;
  }

  return <Outlet />;
}
